import React, { useContext, useEffect, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import { DesignerCard_UserFragment } from '@99designs/apps/spa/pages/create/components/DesignerCard.generated';
import {
    trackBriefModalViewedEvent,
    trackRemovedDesignerEvent,
} from '@99designs/apps/spa/pages/create/events';
import RemoveStructuredBriefDesigners from '@99designs/apps/spa/pages/create/mutations/RemoveStructuredBriefDesigners';
import { BriefContext } from '@99designs/brief';
import { formatMessage } from '@99designs/frontend-components/i18n';
import DesignerConfirmationModal from './DesignerConfirmationModal';
import DesignerSelection from './DesignerSelection';
import LaunchModal, { LaunchState } from './LaunchModal';
import StepFooter, { Props as StepFooterProps } from './StepFooter';

export interface Props {
    stepFooterProps: StepFooterProps;
    launchState?: LaunchState;
    brief: {
        id: string;
        designers: DesignerCard_UserFragment[];
        contacts: DesignerCard_UserFragment[];
        projectIds: number[];
    };
    saving: boolean;
    disableFooterInteraction?: boolean;
}

function BriefFooter(props: Props) {
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const { schema, totalAllowableDesigners, product } = useContext(BriefContext);

    const { stepFooterProps, launchState, brief, saving } = props;
    const isDesignerStep = stepFooterProps.step.isDesignerStep();
    const isShowToolTips = isDesignerStep && !product;

    useEffect(() => {
        if (brief.designers.length === 0) {
            setShowConfirmationModal(false);
        }
    }, [brief.designers.length]);

    if (isDesignerStep) {
        stepFooterProps.disabled = brief.designers.length === 0;
    }

    if (isDesignerStep && !stepFooterProps.step.hasStepAfter()) {
        stepFooterProps.nextAction = () => {
            setShowConfirmationModal(true);
            trackBriefModalViewedEvent({
                id: brief.id,
                modal: 'DesignerConfirmation',
                schema_id: schema,
            });
        };
    }

    return (
        <>
            <StepFooter {...stepFooterProps}>
                <RemoveStructuredBriefDesigners>
                    {(removeDesigners) => (
                        <DesignerSelection
                            brief={brief}
                            showTooltips={isShowToolTips}
                            designers={brief.designers}
                            isDesignerStep={isDesignerStep}
                            onRemoveDesigner={(id) => {
                                trackRemovedDesignerEvent({
                                    id: brief.id,
                                    designer_id: id,
                                });
                                removeDesigners({
                                    id: brief.id,
                                    designerIds: [id],
                                }).catch(Bugsnag.notify);
                            }}
                            selectDesignersCopy={{
                                heading: __('Your designer shortlist'),
                                message: formatMessage(
                                    __(
                                        'When ready, request quote(s) from your chosen designers. You can choose between 1 & {maxDesigners}.'
                                    ),
                                    {
                                        maxDesigners: totalAllowableDesigners,
                                    }
                                ),
                                storageKey: 'designer_selection_designer_placeholder_tooltip',
                            }}
                            confirmDesignersCopy={{
                                heading: __('You’re done!'),
                                message: __(
                                    'Simply confirm your chosen designers to request quote(s) and get underway.'
                                ),
                                storageKey: 'designer_selection_max_designers_tooltip',
                            }}
                            disableInteraction={props.disableFooterInteraction}
                        />
                    )}
                </RemoveStructuredBriefDesigners>
            </StepFooter>
            {launchState && (
                <LaunchModal state={launchState} brief={brief} showing={!!launchState} />
            )}
            <DesignerConfirmationModal
                brief={brief}
                onConfirm={stepFooterProps.launchAction}
                saving={saving}
                onDismiss={() => setShowConfirmationModal(false)}
                showing={showConfirmationModal}
            />
        </>
    );
}

export default BriefFooter;
